import { useState, useEffect, useContext } from 'react'
import { useApolloClient } from '@apollo/client'
import { currency } from '@cred/shared/formatters'

import {
  getVariables,
  isObjectWithKeys,
  getPurchaseUuidFromLocalStorage,
  getRefiUuidFromLocalStorage,
  storeSubmissionInLocalStorage,
  checkForAffectKeys,
} from '../../common/utils'
import {
  getPurchasePrefillData,
  getRefinancePrefillData,
  getNewSubmissionProducts,
} from '../../api/apollo-helpers'
import { PURCHASE_TYPE, REFINANCE_TYPE, DEFAULT_TERMS_ARR } from '../../common/constants'
import InitialValuesContext from '../../../../../components/initial-values-context'

/*
  This is an extracted module that will handle all api-related state management. Below are some details about
  how the hook works and its lifecycle.

  1. Initial component mount will cause us to go fetch the necessary data to render the component. This includes
  querying for form data that is stored by the mortgages-api service as well as the returned products for that
  form data. Data and products for each type -- purchase and refinance -- are then stored in state.

  Note, there are two different types of queries that can run on initialize. We check to see if there is a UUID stored
  for each vertical in localStorage. If so, we use that uuid value to get the most recent submission for form data
  and products. If not, we run the default query to get the default form data and products for each vertical. A user
  can have a UUID stored for one vertical and not the other.

  2. Once the initialization queries complete, the form is updated using the returned `formValues` state. This value is
  used to initialize the redux form 'FilterForm' component.

  3. When a user submits an update, the following occurs. An initial mutation is sent to update the stored form data for that
  user. That mutation will return a state of the submission. In most cases it will be processing via the 'eligiblityInProgress'
  status. If that's the case then we run another query that takes the uuid returned and queries for products of that UUID. All
  this is abstracted via the `getNewSubmissionProducts` function which returns a promise when the entire process is complete. Once
  the promise is resolved, the state of products for that vertical will be updates as well as the initial form values which need
  to be updated for the filter text to render correctly.
*/
const useProductsAndFormHandler = ({ initialValues, partnerFormData, setSubmission = () => {} }) => {
  const [formValues, setFormValues] = useState(initialValues),
    [currentProducts, setCurrentProducts] = useState({
      [PURCHASE_TYPE]: null,
      [REFINANCE_TYPE]: null,
    }),
    [loadingProducts, setLoadingProducts] = useState(true),
    [error, setError] = useState(null),
    { loanType, term } = initialValues,
    client = useApolloClient()

  // Initial values can change due to the async nature of the options provider.
  // If a partner passes this data it will take a few ticks to get to the component,
  // therefore we need to watch for any changes and updated the form accordingly.
  useEffect(() => {
    setFormValues(initialFormValues => ({
      ...initialFormValues,
      ...initialValues,
    }))
  }, [loanType, JSON.stringify(term)])

  const submitFormValues = formData => {
    const { terms = DEFAULT_TERMS_ARR } = initialValues
    setError(null)
    setLoadingProducts(true)

    getNewSubmissionProducts(client, formData, terms)
      .then((res) => {
        const { uuid, products, loanType: submissionLoanType } = res

        setSubmission({ submission: res, vertical: submissionLoanType })
        storeSubmissionInLocalStorage(uuid, submissionLoanType)
        setCurrentProducts(originalCurrentProducts => ({
          ...originalCurrentProducts,
          [submissionLoanType]: products,
        }))
        setFormValues({ ...formData.values })
      })
      .catch(e => {
        /* eslint-disable-next-line */
        console.log(e)
        setError(e)
      })
      .finally(() => {
        setLoadingProducts(false)
      })
  }

  const initializePurchaseForm = ({
    ficoType,
    zipCode,
    estimatedValue,
    downPayment,
    occupancyType,
    propertyType,
    products,
  }) => {
    setFormValues(initialFormValues => ({
      ...initialFormValues,
      ficoType,
      zipCode,
      occupancyType,
      propertyType,
      estimatedValue: currency(estimatedValue),
      downPayment: currency(downPayment),
      purchaseUuid: getPurchaseUuidFromLocalStorage(),
    }))

    setCurrentProducts(originalCurrentProducts => ({
      ...originalCurrentProducts,
      [PURCHASE_TYPE]: products,
    }))
  }

  const initializeRefiForm = ({
    ficoType,
    zipCode,
    homeValue,
    loanAmount,
    occupancyType,
    propertyType,
    products,
    cashOut,
  }) => {
    setFormValues(initialFormValues => ({
      ...initialFormValues,
      ficoTypeRefi: ficoType,
      zipCodeRefi: zipCode,
      occupancyTypeRefi: occupancyType,
      propertyTypeRefi: propertyType,
      homeValue: currency(homeValue),
      loanAmount: currency(loanAmount),
      refiUuid: getRefiUuidFromLocalStorage(),
      cashOut: currency(cashOut || 0),
    }))

    setCurrentProducts(originalCurrentProducts => ({
      ...originalCurrentProducts,
      [REFINANCE_TYPE]: products,
    }))
  }
  const { isJumbo } = useContext(InitialValuesContext) || {}

  useEffect(() => {
    const isPartnerDataExist = isObjectWithKeys(partnerFormData)
    const purchaseUuid = getPurchaseUuidFromLocalStorage()
    const refiUuid = getRefiUuidFromLocalStorage()
    const zipCode = isPartnerDataExist ? partnerFormData.zipCode : null
    const productTypes = isPartnerDataExist && partnerFormData.terms ? partnerFormData.terms.split(',') : DEFAULT_TERMS_ARR
    const isObjectWithAffectKeys = isPartnerDataExist ? checkForAffectKeys(partnerFormData) : false
    const conformingType = isJumbo ? 'nonConforming' : undefined

    const purchasePrefillPromise = !purchaseUuid && isPartnerDataExist && isObjectWithAffectKeys
      ? getNewSubmissionProducts(client, getVariables(partnerFormData, PURCHASE_TYPE), productTypes)
        .then(data => {
          const { uuid, loanType: submissionLoanType } = data
          storeSubmissionInLocalStorage(uuid, submissionLoanType)
          return data
        })
        .then(initializePurchaseForm)
      : getPurchasePrefillData(client, purchaseUuid, zipCode, productTypes, conformingType).then(data => {
        setSubmission({ submission: data, vertical: PURCHASE_TYPE })
        initializePurchaseForm(data)
      })

    const refiPrefillPromise = !refiUuid && isPartnerDataExist && isObjectWithAffectKeys
      ? getNewSubmissionProducts(client, getVariables(partnerFormData, REFINANCE_TYPE), productTypes)
        .then(data => {
          const { uuid, loanType: submissionLoanType } = data
          storeSubmissionInLocalStorage(uuid, submissionLoanType)
          return data
        })
        .then(initializeRefiForm)
      : getRefinancePrefillData(client, refiUuid, zipCode, productTypes, conformingType).then(data => {
        setSubmission({ submission: data, vertical: REFINANCE_TYPE })
        initializeRefiForm(data)
      })

    Promise.all([refiPrefillPromise, purchasePrefillPromise]).catch(e => {
      /* eslint-disable-next-line */
      console.log(e)
      setError(e)
    }).finally(() => {
      setLoadingProducts(false)
    })
  }, [])

  return [submitFormValues, formValues, currentProducts, loadingProducts, error]
}

export default useProductsAndFormHandler
